/**
 * Custom hooks for FaceliftDatePicker component
 */

import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../../../hooks';
import {
	autoCompleteDate,
	filterDateInput,
	formatDateToString,
	formatInputWithAutoSlash,
	parseStringToDate,
} from '../../../../utils/dateUtils';
import {
	createDateForDay,
	generateCalendarDays,
	getNextMonth,
	getPreviousMonth,
} from '../utils/calendarUtils';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export interface DateRange {
	startDate?: string;
	endDate?: string;
}

/**
 * Hook for managing date picker state and logic
 */
export const useDatePicker = (
	value: string = '',
	onChange?: (date: string) => void,
	onRangeChange?: (range: DateRange) => void,
	range?: DateRange,
	isRange: boolean = false,
	disabled: boolean = false,
	minDate?: Date,
	maxDate?: Date,
) => {
	const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
	const [selectedRange, setSelectedRange] = useState<{
		startDate?: Date;
		endDate?: Date;
	}>({});
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);
	const [currentMonth, setCurrentMonth] = useState(() => {
		// Initialize currentMonth based on default values (in NY timezone)
		if (!isRange && value) {
			const parsedDate = parseStringToDate(value);
			return parsedDate || dayjs().tz('America/New_York').startOf('month').toDate();
		}
		if (isRange && range?.startDate) {
			const parsedDate = parseStringToDate(range.startDate);
			return parsedDate || dayjs().tz('America/New_York').startOf('month').toDate();
		}
		return dayjs().tz('America/New_York').startOf('month').toDate();
	});
	const [rangeSelectionState, setRangeSelectionState] = useState<
		'start' | 'end'
	>('start');
	const containerRef = useRef<HTMLDivElement>(null);

	// Update selected date when value prop changes
	useEffect(() => {
		if (!isRange) {
			const parsedDate = parseStringToDate(value);
			setSelectedDate(parsedDate);
			// Update calendar month to show the selected date
			if (parsedDate) {
				setCurrentMonth(parsedDate);
			}
		}
	}, [value, isRange]);

	// Update selected range when range prop changes
	useEffect(() => {
		if (isRange && range) {
			const startDate = range.startDate
				? parseStringToDate(range.startDate)
				: undefined;
			const endDate = range.endDate
				? parseStringToDate(range.endDate)
				: undefined;
			setSelectedRange({ startDate, endDate });
			// Update calendar month to show the start date
			if (startDate) {
				setCurrentMonth(startDate);
			}
		}
	}, [range, isRange]);

	// Handle click outside to close calendar using backdrop approach
	const { backdropProps, contentProps } = useClickOutside(
		() => setIsCalendarOpen(false),
		{ enabled: isCalendarOpen },
	);

	// Calendar icon click handler
	const handleCalendarClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			if (!disabled) {
				setIsCalendarOpen((prev) => !prev);
			}
		},
		[disabled],
	);

	// Input change handler with auto-slash formatting
	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const inputValue = e.target.value;

			if (isRange) {
				// Handle range input (e.g., "MM/DD/YYYY - MM/DD/YYYY")
				const rangeParts = inputValue?.split(' - ');

				if (rangeParts?.length <= 2) {
					let startPart = rangeParts[0] || '';
					let endPart = rangeParts[1] || '';

					// Format start date
					const currentStartLength = (range?.startDate || '').replace(
						/[^\d]/g,
						'',
					).length;
					const newStartLength = startPart.replace(/[^\d]/g, '').length;

					if (newStartLength > currentStartLength) {
						startPart = formatInputWithAutoSlash(startPart);
					} else {
						startPart = filterDateInput(startPart);
					}

					// Format end date if it exists
					if (endPart) {
						const currentEndLength = (range?.endDate || '').replace(
							/[^\d]/g,
							'',
						).length;
						const newEndLength = endPart.replace(/[^\d]/g, '').length;

						if (newEndLength > currentEndLength) {
							endPart = formatInputWithAutoSlash(endPart);
						} else {
							endPart = filterDateInput(endPart);
						}
					}

					// Update range
					const newRange: DateRange = {
						startDate: startPart,
						endDate: endPart || undefined,
					};

					onRangeChange?.(newRange);

					// Update selected range for visual feedback
					const startDate = parseStringToDate(startPart);
					const endDate = parseStringToDate(endPart);
					setSelectedRange({ startDate, endDate });

					if (startDate) {
						setCurrentMonth(startDate);
					}
				}
			} else {
				// Handle single date input
				const currentLength = value.replace(/[^\d]/g, '').length;
				const newLength = inputValue.replace(/[^\d]/g, '').length;

				let formattedValue = inputValue;

				// Only apply auto-formatting if we're adding digits
				if (newLength > currentLength) {
					formattedValue = formatInputWithAutoSlash(inputValue);
				} else {
					// If deleting, just filter out invalid characters
					formattedValue = filterDateInput(inputValue);
				}

				// Update the value
				onChange?.(formattedValue);

				// Try to parse the date as user types
				const parsedDate = parseStringToDate(formattedValue);
				if (parsedDate) {
					// Check if the parsed date is within allowed range (compare in NY timezone)
					const parsedDateNY = dayjs(parsedDate).tz('America/New_York').startOf('day');
					const isValidDate = 
						(!minDate || parsedDateNY.isSameOrAfter(dayjs(minDate).tz('America/New_York').startOf('day'), 'day')) && 
						(!maxDate || parsedDateNY.isSameOrBefore(dayjs(maxDate).tz('America/New_York').startOf('day'), 'day'));
					
					if (isValidDate) {
						setSelectedDate(parsedDate);
						setCurrentMonth(parsedDate); // Update calendar month to match typed date
					}
				}
			}
		},
		[onChange, onRangeChange, value, range, isRange, minDate, maxDate],
	);

	// Input blur handler with auto-completion
	const handleInputBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			const inputValue = e.target.value.trim();

			if (inputValue && !parseStringToDate(inputValue)) {
				// If input has value but isn't a valid date, try to auto-complete
				const completedDate = autoCompleteDate(inputValue);

				// Update the value and call onChange
				if (onChange && completedDate !== inputValue) {
					onChange(completedDate);
				}

				// Try to parse the completed date
				const parsedDate = parseStringToDate(completedDate);
				if (parsedDate) {
					setSelectedDate(parsedDate);
					setCurrentMonth(parsedDate);
				}
			}
		},
		[onChange],
	);

	// Input focus handler
	const handleInputFocus = useCallback(() => {
		// Don't auto-open calendar when focusing to type
	}, []);

	// Input click handler
	const handleInputClick = useCallback(() => {
		// Don't auto-open calendar when clicking input to edit
	}, []);

	// Date selection handler
	const handleDateSelect = useCallback(
		(day: number) => {
			const newDate = createDateForDay(currentMonth, day);

			// Check if date is within allowed range (compare in NY timezone)
			if (minDate) {
				const newDateNY = dayjs(newDate).tz('America/New_York').startOf('day');
				const minDateNY = dayjs(minDate).tz('America/New_York').startOf('day');
				if (newDateNY.isBefore(minDateNY, 'day')) return;
			}
			if (maxDate) {
				const newDateNY = dayjs(newDate).tz('America/New_York').startOf('day');
				const maxDateNY = dayjs(maxDate).tz('America/New_York').startOf('day');
				if (newDateNY.isAfter(maxDateNY, 'day')) return;
			}

			if (isRange) {
				// Handle range selection
				if (rangeSelectionState === 'start' || !selectedRange.startDate) {
					// Setting start date
					setSelectedRange({ startDate: newDate, endDate: undefined });
					setRangeSelectionState('end');

					if (onRangeChange) {
						const formattedDate = formatDateToString(newDate);
						onRangeChange({ startDate: formattedDate, endDate: undefined });
					}
				} else {
					// Setting end date
					const { startDate } = selectedRange;

					// Ensure end date is after start date (compare in NY timezone)
					const newDateNY = dayjs(newDate).tz('America/New_York').startOf('day');
					const startDateNY = startDate ? dayjs(startDate).tz('America/New_York').startOf('day') : null;
					if (startDateNY && newDateNY.isSameOrAfter(startDateNY, 'day')) {
						setSelectedRange({ startDate, endDate: newDate });
						setRangeSelectionState('start');
						// Don't close calendar for range selection - let user click outside

						if (onRangeChange) {
							const formattedStartDate = formatDateToString(startDate);
							const formattedEndDate = formatDateToString(newDate);
							onRangeChange({
								startDate: formattedStartDate,
								endDate: formattedEndDate,
							});
						}
					} else {
						// If end date is before start date, reset to new start date
						setSelectedRange({ startDate: newDate, endDate: undefined });
						setRangeSelectionState('end');

						if (onRangeChange) {
							const formattedDate = formatDateToString(newDate);
							onRangeChange({ startDate: formattedDate, endDate: undefined });
						}
					}
				}
			} else {
				// Handle single date selection
				setSelectedDate(newDate);
				setIsCalendarOpen(false);

				if (onChange) {
					const formattedDate = formatDateToString(newDate);
					onChange(formattedDate);
				}
			}
		},
		[
			currentMonth,
			onChange,
			onRangeChange,
			isRange,
			rangeSelectionState,
			selectedRange,
			minDate,
			maxDate,
		],
	);

	// Month navigation handlers
	const handlePrevMonth = useCallback(() => {
		setCurrentMonth((prev) => getPreviousMonth(prev));
	}, []);

	const handleNextMonth = useCallback(() => {
		setCurrentMonth((prev) => getNextMonth(prev));
	}, []);

	// Calendar days generation
	const calendarDays = generateCalendarDays(currentMonth);

	return {
		// State
		selectedDate,
		selectedRange,
		isCalendarOpen,
		currentMonth,
		calendarDays,
		containerRef,

		// Handlers
		handleCalendarClick,
		handleInputChange,
		handleInputBlur,
		handleInputFocus,
		handleInputClick,
		handleDateSelect,
		handlePrevMonth,
		handleNextMonth,

		// Click outside props
		backdropProps,
		contentProps,

		// Utilities
		setIsCalendarOpen,
	};
};

/**
 * Hook for managing calendar day state
 */
export const useCalendarDay = (
	currentMonth: Date,
	day: number,
	selectedDate?: Date,
	selectedRange?: { startDate?: Date; endDate?: Date },
	isRange?: boolean,
) => {
	const dayDate = createDateForDay(currentMonth, day);

	// Use dayjs for timezone-aware comparisons
	const dayDateNY = dayjs(dayDate).tz('America/New_York').startOf('day');
	const selectedDateNY = selectedDate ? dayjs(selectedDate).tz('America/New_York').startOf('day') : null;
	const todayNY = dayjs().tz('America/New_York').startOf('day');

	const isSelected = selectedDateNY ? dayDateNY.isSame(selectedDateNY, 'day') : false;
	const isToday = dayDateNY.isSame(todayNY, 'day');

	// Range-specific logic (using NY timezone)
	const startDateNY = selectedRange?.startDate ? dayjs(selectedRange.startDate).tz('America/New_York').startOf('day') : null;
	const endDateNY = selectedRange?.endDate ? dayjs(selectedRange.endDate).tz('America/New_York').startOf('day') : null;

	const isRangeStart =
		isRange &&
		startDateNY &&
		dayDateNY.isSame(startDateNY, 'day');
	const isRangeEnd =
		isRange &&
		endDateNY &&
		dayDateNY.isSame(endDateNY, 'day');

	const isInRange =
		isRange &&
		startDateNY &&
		endDateNY &&
		dayDateNY.isSameOrAfter(startDateNY, 'day') &&
		dayDateNY.isSameOrBefore(endDateNY, 'day') &&
		!isRangeStart &&
		!isRangeEnd;

	return {
		isSelected,
		isToday,
		dayDate,
		isInRange: !!isInRange,
		isRangeStart: !!isRangeStart,
		isRangeEnd: !!isRangeEnd,
	};
};
